<script setup lang="ts">
import { ref } from 'vue';

import type { MapProperties } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import { MapComponent } from '@apps/main/new-desktop/modules/maps';

import { AppButton } from '@libs/ui';
import type { Map, MapTypeId } from '@libs/maps';
import { MapControl } from '@libs/maps';

interface CarTripMapProps {
  mapProperties: MapProperties;
  shouldLoadMap: boolean;
}
const props = defineProps<CarTripMapProps>();

interface CarTripMapEmits {
  (event: 'set-dynamic-map-integration', integration: Map): void;
  (event: 'map-type-id-changed', value: MapTypeId): void;
}
const emit = defineEmits<CarTripMapEmits>();

const shouldShowFullScreenMap = ref(false);
</script>

<template>
  <div
    class="car-trip-map"
    :class="{
      [shouldShowFullScreenMap ? 'expanded' : 'collapsed']: true,
    }"
  >
    <div
      class="car-trip-map-wrapper"
      :class="{
        loading: props.shouldLoadMap,
      }"
    >
      <MapComponent
        :marker-formatters="props.mapProperties.markerFormatters"
        :markers="props.mapProperties.markers"
        :route="props.mapProperties.route"
        :show-all-markers="props.mapProperties.showAllMarkers"
        :start-rectangle="props.mapProperties.startRectangle"
        :gesture-handling="props.mapProperties.gestureHandling"
        :locale="props.mapProperties.locale"
        :map-controls="[MapControl.zoomControl, MapControl.mapTypeControl]"
        @map-created="emit('set-dynamic-map-integration', $event as Map)"
        @on-map-type-id-changed="emit('map-type-id-changed', $event)"
      />
      <AppButton
        v-if="shouldShowFullScreenMap"
        class="collapse-button"
        type="secondary"
        state="enabled"
        size="small"
        :icon="{
          name: 'close',
        }"
        :aria-label="$t('a11y.minimise_map')"
        @click="shouldShowFullScreenMap = false"
      />

      <AppButton
        v-if="!shouldShowFullScreenMap"
        class="expand-button"
        type="secondary"
        state="enabled"
        size="small"
        :icon="{
          name: 'zoom',
        }"
        :aria-label="$t('a11y.maximise_map')"
        @click="shouldShowFullScreenMap = true"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.car-trip-map {
  &.collapsed {
    margin: 0 calc(var(--side-padding) * -1);
    width: calc(100% + var(--side-padding) * 2);
    height: 100%;
  }

  &.expanded {
    height: 100vh;
    height: 100svh;
    width: 100vw;
    position: fixed;
    z-index: var(--modal-z-index);
    top: 0;
    left: 0;
  }

  .car-trip-map-wrapper {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;

    &.loading::after {
      @include animated-background-secondary;
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
    }

    &::after {
      content: none;
    }

    .collapse-button,
    .expand-button {
      position: absolute;
      top: var(--spacing-12);
      right: var(--spacing-12);
    }
  }
}
</style>

<style lang="scss">
.car-trip-map {
  @include map-type-switch(var(--spacing-24));
  @include map-zoom-control(34px, 36px, -40px, -90px);
}
</style>
